import { useRequiredContext } from "@redotech/react-util/context";
import { ScreenSize } from "@redotech/redo-model/email-builder";
import { FulfillmentOrder } from "@redotech/redo-model/fulfillment";
import { OrderTrackingShipmentStatus } from "@redotech/redo-model/order-tracking/order-tracking-types";
import {
  TrackingPageSettings,
  TrackingPageTrackingDetailsBlock,
} from "@redotech/redo-model/order-tracking/tracking-page-definition";
import type {
  TrackingPageOrder,
  TrackingPageShipment,
} from "@redotech/redo-model/order-tracking/tracking-page-order-definition";
import { memo, useMemo } from "react";
import { useParams } from "react-router-dom";
import { pickUpConfig } from "../../../settings-elements/order/pick-up-config-util";
import {
  TrackingPageContext,
  TrackingPageEnvironment,
} from "../../context/tracking-page-context";
import { BlockWrapper } from "../shared/block-wrapper";
import {
  getSelectedShipmentWithFallback,
  getShipmentIndex,
} from "../shared/fulfillment-utils";
import { CustomPickupBlock } from "./custom-pickup";
import { getPreviewOrder, getPreviewShipment } from "./preview-mock-data";
import { TrackingDetailsDesktop } from "./tracking-details-desktop";
import { TrackingDetailsMobile } from "./tracking-details-mobile";

// In the customer portal there's no `screenSize` from context, so fall back
// to the historical window-width threshold that the rest of this block uses.
// In the builder we honor the explicit screen-size toggle so the iframe-style
// mobile preview actually exercises the mobile layout.
function shouldRenderMobile(
  environment: TrackingPageEnvironment,
  screenSize: ScreenSize | undefined,
) {
  if (environment === TrackingPageEnvironment.BUILDER) {
    return screenSize === ScreenSize.MOBILE;
  }
  return typeof window !== "undefined" && window.innerWidth <= 1000;
}

export const TrackingDetails = memo(function TrackingDetails({
  optionalSubtext,
}: TrackingPageTrackingDetailsBlock) {
  const {
    order,
    environment,
    fulfillmentOrders,
    pageSettings,
    stalledDays,
    screenSize,
  } = useRequiredContext(TrackingPageContext);

  const { fulfillmentIndex: paramsFulfillmentIndex } = useParams<{
    fulfillmentIndex?: string;
  }>();

  const isBuilder = environment === TrackingPageEnvironment.BUILDER;

  // In the page builder we substitute realistic mock data when the merchant
  // hasn't supplied a sample order — and when the supplied shipment lacks a
  // timeline / ETA / tracking code. This way the live block renders the same
  // way it does for a real customer, instead of collapsing to a bare "Status".
  const previewOrder = useMemo(
    () => (isBuilder && !order ? getPreviewOrder() : order),
    [isBuilder, order],
  );

  const shipmentIndex = getShipmentIndex(
    paramsFulfillmentIndex,
    environment,
    previewOrder,
  );
  const selectedShipment = getSelectedShipmentWithFallback(
    previewOrder?.shipments,
    shipmentIndex,
  );

  const previewShipment = useMemo(() => {
    if (!isBuilder) return selectedShipment;
    if (!selectedShipment || selectedShipment.trackingEvents.length === 0) {
      return getPreviewShipment();
    }
    return selectedShipment;
  }, [isBuilder, selectedShipment]);

  const robustShipment: TrackingPageShipment | undefined = useMemo(() => {
    if (!previewShipment) return undefined;
    if (shouldHaveBeenDeliveredButWasnt(previewShipment)) {
      return { ...previewShipment, estimatedDeliveryAt: null };
    }
    return previewShipment;
  }, [previewShipment]);

  return (
    <BlockWrapper
      environment={environment}
      loading={!previewOrder}
      pageSettings={pageSettings}
      withBorder
    >
      {previewOrder && (
        <TrackingDetailsForShipment
          fulfillmentOrders={fulfillmentOrders}
          optionalSubtext={optionalSubtext}
          order={previewOrder}
          pageSettings={pageSettings}
          screenSize={screenSize}
          shipment={robustShipment}
          stalledDays={stalledDays}
        />
      )}
    </BlockWrapper>
  );
});

const TrackingDetailsForShipment = memo(function TrackingDetailsForShipment({
  shipment,
  order,
  fulfillmentOrders,
  pageSettings,
  optionalSubtext,
  stalledDays,
  screenSize,
}: {
  shipment: TrackingPageShipment | undefined;
  order: TrackingPageOrder;
  fulfillmentOrders: FulfillmentOrder[];
  pageSettings: TrackingPageSettings | undefined;
  optionalSubtext: string | undefined;
  stalledDays: number | null;
  screenSize: ScreenSize | undefined;
}) {
  const { returnAppSettings, environment } =
    useRequiredContext(TrackingPageContext);

  const pickupConfig = pickUpConfig(fulfillmentOrders);

  const isPosOrder = order.sourceName === "pos";
  if (isPosOrder) {
    return null;
  }

  const shouldShowAndarPickup =
    returnAppSettings.storeUrl === "andar-wallets.myshopify.com" &&
    shipment &&
    shipment.carrier === "Self-Pickup (by PDQ)";
  const shouldShowIvyCityPickup =
    returnAppSettings.storeUrl === "ivy-city-co.myshopify.com" &&
    pickupConfig.isPickUpOrder;
  if (shouldShowAndarPickup || shouldShowIvyCityPickup) {
    return (
      <CustomPickupBlock
        isPickUpOrder={pickupConfig.isPickUpOrder}
        pageSettings={pageSettings}
        shipment={shipment}
        storeUrl={returnAppSettings.storeUrl}
      />
    );
  }

  if (shouldRenderMobile(environment, screenSize)) {
    return (
      <TrackingDetailsMobile
        optionalSubtext={optionalSubtext}
        order={order}
        pickupConfig={pickupConfig}
        shipment={shipment}
        stalledDays={stalledDays}
      />
    );
  }

  return (
    <TrackingDetailsDesktop
      optionalSubtext={optionalSubtext}
      order={order}
      pickupConfig={pickupConfig}
      shipment={shipment}
      stalledDays={stalledDays}
    />
  );
});

function shouldHaveBeenDeliveredButWasnt(shipment: TrackingPageShipment) {
  return !!(
    shipment.status !== OrderTrackingShipmentStatus.DELIVERED &&
    shipment.estimatedDeliveryAt &&
    Temporal.Instant.compare(
      Temporal.Now.zonedDateTimeISO().toInstant(),
      shipment.estimatedDeliveryAt.toTemporalInstant(),
    ) > 0
  );
}
